import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import VerificationPrompt from '../components/VerificationPrompt';
import VerificationPurchaseModal from '../components/VerificationPurchaseModal';
import VerificationBadge from '../components/VerificationBadge';
import { 
  ClockIcon,
  CalendarDaysIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XMarkIcon,
  StarIcon,
  CogIcon,
  HomeIcon,
  BellIcon,
  MapPinIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/solid';

interface TeacherData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  instrumentsTaught: string[];
  specialtyInstrument: string;
  experience: string;
  agesTaught: string[];
  specializedAgeGroup: string;
  teachingSpecialties: string[];
  specializedTeachingAreas: string[];
  languagesSpoken: string[];
  locationsOffered: string[];
  maxTravelDistance?: string;
}

interface LessonRequest {
  id: string;
  studentName: string;
  studentAge: string;
  instrument: string;
  skillLevel: string;
  requestedDate: string;
  requestedTime: string;
  lessonType: string;
  duration: number;
  specialRequests?: string;
  requestDate: string;
  status: 'pending' | 'approved' | 'declined';
  hourlyRate: number;
}

interface ConfirmedBooking {
  id: string;
  studentName: string;
  instrument: string;
  date: string;
  time: string;
  duration: number;
  lessonType: string;
  totalPrice: number;
  status: 'upcoming' | 'completed' | 'cancelled';
}

// Mock data for lesson requests
const mockLessonRequests: LessonRequest[] = [
  {
    id: '1',
    studentName: 'Emma Thompson',
    studentAge: '12 years old',
    instrument: 'Piano',
    skillLevel: 'Beginner',
    requestedDate: 'Monday',
    requestedTime: '4:00 PM',
    lessonType: 'Online',
    duration: 30,
    specialRequests: 'Would like to focus on classical pieces and basic music theory',
    requestDate: '2 hours ago',
    status: 'pending',
    hourlyRate: 75
  },
  {
    id: '2',
    studentName: 'Marcus Chen',
    studentAge: '25 years old',
    instrument: 'Piano',
    skillLevel: 'Intermediate',
    requestedDate: 'Wednesday',
    requestedTime: '7:00 PM',
    lessonType: 'On-site (teacher\'s studio)',
    duration: 60,
    specialRequests: 'Looking to improve jazz improvisation skills',
    requestDate: '1 day ago',
    status: 'pending',
    hourlyRate: 75
  },
  {
    id: '3',
    studentName: 'Sarah Kim',
    studentAge: '16 years old',
    instrument: 'Voice/Vocals',
    skillLevel: 'Intermediate',
    requestedDate: 'Friday',
    requestedTime: '5:00 PM',
    lessonType: 'Online',
    duration: 30,
    requestDate: '3 days ago',
    status: 'approved',
    hourlyRate: 75
  }
];

// Mock data for confirmed bookings
const mockConfirmedBookings: ConfirmedBooking[] = [
  {
    id: '1',
    studentName: 'Alex Rivera',
    instrument: 'Piano',
    date: 'Today',
    time: '2:00 PM',
    duration: 30,
    lessonType: 'Online',
    totalPrice: 37.50,
    status: 'upcoming'
  },
  {
    id: '2',
    studentName: 'Jessica Wong',
    instrument: 'Voice/Vocals',
    date: 'Tomorrow',
    time: '4:00 PM',
    duration: 60,
    lessonType: 'On-site (teacher\'s studio)',
    totalPrice: 75.00,
    status: 'upcoming'
  },
  {
    id: '3',
    studentName: 'David Martinez',
    instrument: 'Piano',
    date: 'Last Tuesday',
    time: '6:00 PM',
    duration: 30,
    lessonType: 'Online',
    totalPrice: 37.50,
    status: 'completed'
  }
];

const TeacherDashboardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { teacherData } = location.state || {};
  const [activeTab, setActiveTab] = useState('overview');
  const [lessonRequests, setLessonRequests] = useState<LessonRequest[]>(mockLessonRequests);
  const [confirmedBookings] = useState<ConfirmedBooking[]>(mockConfirmedBookings);
  
  // Verification state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [user, setUser] = useState<any>(null);

  // Log dashboard access
  console.log('Teacher dashboard loaded for:', teacherData);

  // Initialize user data (in a real app, this would come from context/auth)
  React.useEffect(() => {
    // Mock user data - in real app this would come from auth context
    const mockUser = {
      id: '1',
      email: teacherData?.email || 'teacher@example.com',
      firstName: teacherData?.firstName || 'John',
      lastName: teacherData?.lastName || 'Doe',
      role: 'teacher',
      verification: {
        isVerified: false,
        status: 'unverified',
        level: 'basic'
      }
    };
    setUser(mockUser);
    console.log('🔧 Teacher user initialized:', mockUser.email);
  }, [teacherData]);

  if (!teacherData) {
    console.warn('No teacher data found, redirecting to teacher profile');
    navigate('/teacher-profile');
    return null;
  }

  const pendingRequestsCount = lessonRequests.filter(req => req.status === 'pending').length;
  const upcomingLessonsCount = confirmedBookings.filter(booking => booking.status === 'upcoming').length;
  const totalEarnings = confirmedBookings
    .filter(booking => booking.status === 'completed')
    .reduce((total, booking) => total + booking.totalPrice, 0);

  const handleRequestAction = (requestId: string, action: 'approve' | 'decline') => {
    setLessonRequests(prev => 
      prev.map(req => 
        req.id === requestId 
          ? { ...req, status: action === 'approve' ? 'approved' : 'declined' }
          : req
      )
    );
    console.log(`${action === 'approve' ? 'Approved' : 'Declined'} lesson request:`, requestId);
    
    if (action === 'approve') {
      // TODO: Add to confirmed bookings and send confirmation to student
      alert('Lesson request approved! The student will be notified.');
    } else {
      // TODO: Send decline notification to student
      alert('Lesson request declined. The student will be notified.');
    }
  };

  const handleContactStudent = (studentName: string) => {
    console.log('Opening contact with student:', studentName);
    navigate('/messages', { 
      state: { 
        openConversationWith: studentName,
        userType: 'student'
      }
    });
  };

  // Verification handlers
  const handleStartVerification = () => {
    console.log('🔔 Starting verification process for teacher');
    setShowVerificationModal(true);
  };

  const handleVerificationSuccess = () => {
    console.log('✅ Verification purchase completed');
    // Update user verification status
    setUser((prev: any) => ({
      ...prev,
      verification: {
        ...prev.verification,
        status: 'pending'
      }
    }));
    setShowVerificationModal(false);
    // Show success message or refresh verification status
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: HomeIcon },
    { id: 'requests', label: `Requests (${pendingRequestsCount})`, icon: BellIcon },
    { id: 'bookings', label: 'My Lessons', icon: CalendarDaysIcon },
    { id: 'messages', label: 'Messages', icon: ChatBubbleLeftRightIcon },
    { id: 'profile', label: 'Profile', icon: UserIcon },
    { id: 'settings', label: 'Settings', icon: CogIcon }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                Welcome back, {teacherData.firstName}!
                {user?.verification?.isVerified && (
                  <VerificationBadge 
                    isVerified={true} 
                    size="lg" 
                    className="ml-3" 
                  />
                )}
              </h2>
            </div>

            {/* Verification Prompt */}
            {user && (
              <VerificationPrompt 
                user={user}
                onStartVerification={handleStartVerification}
                className="mb-6"
              />
            )}

            {/* Profile Completion Prompt */}
            {teacherData?.needsQuestionnaire && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <UserIcon className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                      Complete Your Teaching Profile
                    </h3>
                    <p className="text-yellow-700 mb-4">
                      Add detailed information about your teaching experience, instruments, and preferences to help students find you more easily.
                    </p>
                    <button
                      onClick={() => navigate('/teacher-questionnaire', { state: { experience: teacherData.experience || 'Experienced' } })}
                      className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-semibold"
                    >
                      Complete Profile Questionnaire
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BellIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                    <p className="text-2xl font-semibold text-gray-900">{pendingRequestsCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CalendarDaysIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Upcoming Lessons</p>
                    <p className="text-2xl font-semibold text-gray-900">{upcomingLessonsCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <CurrencyDollarIcon className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">This Month</p>
                    <p className="text-2xl font-semibold text-gray-900">${totalEarnings.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {lessonRequests.slice(0, 3).map((request) => (
                    <div key={request.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <UserIcon className="w-8 h-8 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{request.studentName}</p>
                          <p className="text-sm text-gray-600">
                            Requested {request.instrument} lesson • {request.requestDate}
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        request.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {request.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'requests':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Lesson Requests</h2>
              <p className="text-gray-600">Review and respond to incoming lesson requests from students.</p>
            </div>

            <div className="space-y-4">
              {lessonRequests.map((request) => (
                <div key={request.id} className="bg-white rounded-lg shadow border overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserIcon className="w-6 h-6 text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{request.studentName}</h3>
                            <span className="text-sm text-gray-500">({request.studentAge})</span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              request.status === 'approved' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {request.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-sm font-medium text-gray-600">Instrument</p>
                              <p className="text-sm text-gray-900">{request.instrument}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Skill Level</p>
                              <p className="text-sm text-gray-900">{request.skillLevel}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Date & Time</p>
                              <p className="text-sm text-gray-900">{request.requestedDate}, {request.requestedTime}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Duration & Price</p>
                              <p className="text-sm text-gray-900">
                                {request.duration} min • ${(request.duration === 30 ? request.hourlyRate / 2 : request.hourlyRate).toFixed(2)}
                              </p>
                            </div>
                          </div>

                          <div className="mb-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <MapPinIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-600">Lesson Type:</span>
                              <span className="text-sm text-gray-900">{request.lessonType}</span>
                            </div>
                          </div>

                          {request.specialRequests && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-600 mb-1">Special Requests:</p>
                              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{request.specialRequests}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {request.status === 'pending' && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t">
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleRequestAction(request.id, 'approve')}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>Approve</span>
                          </button>
                          <button
                            onClick={() => handleRequestAction(request.id, 'decline')}
                            className="flex items-center space-x-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <XMarkIcon className="w-4 h-4" />
                            <span>Decline</span>
                          </button>
                        </div>
                        <button
                          onClick={() => handleContactStudent(request.studentName)}
                          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <ChatBubbleLeftRightIcon className="w-4 h-4" />
                          <span>Message</span>
                        </button>
                      </div>
                    )}

                    {request.status === 'approved' && (
                      <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 text-green-600">
                            <CheckCircleIcon className="w-5 h-5" />
                            <span className="font-medium">Approved - Lesson confirmed</span>
                          </div>
                          <button
                            onClick={() => handleContactStudent(request.studentName)}
                            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <ChatBubbleLeftRightIcon className="w-4 h-4" />
                            <span>Message Student</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {request.status === 'declined' && (
                      <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center space-x-2 text-red-600">
                          <XMarkIcon className="w-5 h-5" />
                          <span className="font-medium">Declined</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {lessonRequests.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <BellIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No pending requests</h3>
                  <p className="text-gray-600">New lesson requests will appear here.</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'bookings':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">My Lessons</h2>
              <p className="text-gray-600">Manage your confirmed and completed lessons.</p>
            </div>

            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Lessons</h3>
              </div>
              <div className="divide-y">
                {confirmedBookings
                  .filter(booking => booking.status === 'upcoming')
                  .map((booking) => (
                  <div key={booking.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{booking.studentName}</h4>
                          <p className="text-sm text-gray-600">
                            {booking.instrument} • {booking.duration} minutes • {booking.lessonType}
                          </p>
                          <p className="text-sm text-gray-600">
                            {booking.date} at {booking.time}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">${booking.totalPrice.toFixed(2)}</p>
                        <button
                          onClick={() => handleContactStudent(booking.studentName)}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          Message
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Recent Lessons</h3>
              </div>
              <div className="divide-y">
                {confirmedBookings
                  .filter(booking => booking.status === 'completed')
                  .map((booking) => (
                  <div key={booking.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{booking.studentName}</h4>
                          <p className="text-sm text-gray-600">
                            {booking.instrument} • {booking.duration} minutes • {booking.lessonType}
                          </p>
                          <p className="text-sm text-gray-600">
                            {booking.date} at {booking.time}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">${booking.totalPrice.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">Completed</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Information</h2>
              <p className="text-gray-600">View and edit your teaching profile.</p>
            </div>

            <div className="bg-white rounded-lg shadow border p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Name</p>
                      <p className="text-sm text-gray-900">{teacherData.firstName} {teacherData.lastName}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Email</p>
                      <p className="text-sm text-gray-900">{teacherData.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Phone</p>
                      <p className="text-sm text-gray-900">{teacherData.phoneNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Experience</p>
                      <p className="text-sm text-gray-900">{teacherData.experience}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Teaching Profile</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Instruments</p>
                      <p className="text-sm text-gray-900">{teacherData.instrumentsTaught?.join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Specialty</p>
                      <p className="text-sm text-gray-900">{teacherData.specialtyInstrument}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Age Groups</p>
                      <p className="text-sm text-gray-900">{teacherData.agesTaught?.join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Languages</p>
                      <p className="text-sm text-gray-900">{teacherData.languagesSpoken?.join(', ')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t">
                <button
                  onClick={() => navigate('/teacher-questionnaire', { state: { edit: true, teacherData } })}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit Profile
                </button>
              </div>
            </div>
          </div>
        );

      case 'messages':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Messages</h2>
              <p className="text-gray-600">Communicate with your students and their parents.</p>
            </div>

            <div className="bg-white rounded-lg shadow border p-8 text-center">
              <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Access Full Messaging</h3>
              <p className="text-gray-600 mb-6">
                Use our dedicated messaging interface to communicate with students and parents.
              </p>
              <button
                onClick={() => navigate('/messages')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Open Messages
              </button>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Settings</h2>
              <p className="text-gray-600">Manage your account preferences and settings.</p>
            </div>

            <div className="bg-white rounded-lg shadow border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notifications</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Email notifications</p>
                    <p className="text-sm text-gray-600">Receive email alerts for new lesson requests</p>
                  </div>
                  <input type="checkbox" defaultChecked className="rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">SMS notifications</p>
                    <p className="text-sm text-gray-600">Receive text messages for urgent updates</p>
                  </div>
                  <input type="checkbox" className="rounded" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account</h3>
              <div className="space-y-4">
                <button className="text-blue-600 hover:text-blue-700">Change Password</button>
                <button className="text-red-600 hover:text-red-700">Deactivate Account</button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gray-50">
        
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <h1 className="text-xl font-semibold text-gray-900">Teacher Dashboard</h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {teacherData.firstName} {teacherData.lastName}
                </span>
                <button
                  onClick={() => navigate('/')}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <HomeIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Sidebar */}
            <div className="lg:w-64 flex-shrink-0">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Verification Purchase Modal */}
      {user && (
        <VerificationPurchaseModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onSuccess={handleVerificationSuccess}
          user={user}
        />
      )}
    </MinimalLayout>
  );
};

export default TeacherDashboardPage; 